import { z } from "zod";

import { entityIdSchema, isoInstantSchema, localeSchema, slugSchema } from "./primitives";

/**
 * LLM provider settings (SPEC §74 "Future Architecture", added by explicit
 * user request).
 *
 * A *provider profile* describes one OpenAI-compatible endpoint: base URL,
 * API key, optional API version and default model. These are credentials the
 * CMS stores in D1 so the Worker can call the model on the site's behalf
 * (writing assistance, translation, SEO — SPEC §74) without the browser ever
 * seeing a key.
 *
 * The wire type never carries the raw key — only a mask. Anything that needs
 * the key stays server-side in cms-api.
 */

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

/** A pre-configured provider template the Admin form fills from. */
export const llmPresetSchema = z
  .object({
    /** Stable slug, e.g. `openai`, `azure-openai`, `dashscope`. */
    id: z
      .string()
      .regex(/^[a-z0-9][a-z0-9-]*$/, "Preset ids are lowercase, hyphenated slugs")
      .min(1)
      .max(50),
    /** Brand display name. Language-neutral on purpose. */
    name: z.string().min(1).max(100),
    /** OpenAI-compatible root, e.g. `https://api.openai.com/v1`. Empty only for the custom preset. */
    baseUrl: z.string().max(500),
    /** Azure-style API version query, when the provider needs one. */
    apiVersion: z.string().max(50).nullable(),
    /** Suggested default model id. */
    defaultModel: z.string().max(200).nullable(),
    /** Suggested model ids for the form's datalist — suggestions, not a whitelist. */
    models: z.array(z.string().min(1).max(200)).max(50),
    /** False only for local runtimes such as Ollama. */
    apiKeyRequired: z.boolean(),
  })
  .strict();

export type LlmPreset = z.infer<typeof llmPresetSchema>;

/**
 * Pre-configured common providers.
 *
 * `baseUrl` points at each vendor's *OpenAI-compatible* surface (DashScope's
 * compatible mode, Azure's `/openai/v1`, …), because that is the format this
 * page manages. `models` are editable suggestions — providers add models
 * faster than this list can follow.
 */
export const LLM_PRESETS: readonly LlmPreset[] = [
  {
    id: "openai",
    name: "OpenAI",
    baseUrl: "https://api.openai.com/v1",
    apiVersion: null,
    defaultModel: "gpt-4o",
    models: ["gpt-4o", "gpt-4o-mini", "gpt-4.1", "o3-mini"],
    apiKeyRequired: true,
  },
  {
    id: "azure-openai",
    name: "Azure OpenAI",
    baseUrl: "https://{resource-name}.openai.azure.com/openai/v1",
    apiVersion: null,
    defaultModel: "gpt-4o",
    models: ["gpt-4o", "gpt-4o-mini", "gpt-4.1"],
    apiKeyRequired: true,
  },
  {
    id: "dashscope",
    name: "阿里云百炼 (DashScope)",
    baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    apiVersion: null,
    defaultModel: "qwen-plus",
    models: ["qwen-max", "qwen-plus", "qwen-turbo", "qwen-long"],
    apiKeyRequired: true,
  },
  {
    id: "deepseek",
    name: "DeepSeek",
    baseUrl: "https://api.deepseek.com/v1",
    apiVersion: null,
    defaultModel: "deepseek-chat",
    models: ["deepseek-chat", "deepseek-reasoner"],
    apiKeyRequired: true,
  },
  {
    id: "moonshot",
    name: "Moonshot AI (Kimi)",
    baseUrl: "https://api.moonshot.cn/v1",
    apiVersion: null,
    defaultModel: "moonshot-v1-32k",
    models: ["moonshot-v1-8k", "moonshot-v1-32k", "moonshot-v1-128k"],
    apiKeyRequired: true,
  },
  {
    id: "zhipu",
    name: "智谱 GLM",
    baseUrl: "https://open.bigmodel.cn/api/paas/v4",
    apiVersion: null,
    defaultModel: "glm-4-plus",
    models: ["glm-4-plus", "glm-4-air", "glm-4-flash"],
    apiKeyRequired: true,
  },
  {
    id: "volcengine-ark",
    name: "火山方舟 (豆包)",
    baseUrl: "https://ark.cn-beijing.volces.com/api/v3",
    apiVersion: null,
    defaultModel: "doubao-seed-1-6",
    models: ["doubao-seed-1-6", "doubao-1-5-pro-32k"],
    apiKeyRequired: true,
  },
  {
    id: "siliconflow",
    name: "SiliconFlow 硅基流动",
    baseUrl: "https://api.siliconflow.cn/v1",
    apiVersion: null,
    defaultModel: "deepseek-ai/DeepSeek-V3",
    models: ["deepseek-ai/DeepSeek-V3", "Qwen/Qwen2.5-72B-Instruct"],
    apiKeyRequired: true,
  },
  {
    id: "openrouter",
    name: "OpenRouter",
    baseUrl: "https://openrouter.ai/api/v1",
    apiVersion: null,
    defaultModel: "openai/gpt-4o",
    models: ["openai/gpt-4o", "anthropic/claude-3.5-sonnet"],
    apiKeyRequired: true,
  },
  {
    id: "ollama",
    name: "Ollama (本地)",
    baseUrl: "http://localhost:11434/v1",
    apiVersion: null,
    defaultModel: "llama3.1",
    models: ["llama3.1", "qwen2.5"],
    apiKeyRequired: false,
  },
  {
    id: "custom",
    name: "自定义 (OpenAI 兼容)",
    baseUrl: "",
    apiVersion: null,
    defaultModel: null,
    models: [],
    apiKeyRequired: true,
  },
];

/** Look up a preset by id, or `undefined` when there is no such preset. */
export function findLlmPreset(presetId: string): LlmPreset | undefined {
  return LLM_PRESETS.find((preset) => preset.id === presetId);
}

// ---------------------------------------------------------------------------
// Provider profile
// ---------------------------------------------------------------------------

/** `https?://` URL — `http` is allowed for local runtimes such as Ollama. */
export const llmBaseUrlSchema = z
  .string()
  .min(1)
  .max(500)
  .refine(
    (value) => {
      try {
        const url = new URL(value);
        return url.protocol === "https:" || url.protocol === "http:";
      } catch {
        return false;
      }
    },
    { message: "Must be an http(s) URL" },
  );

/**
 * A stored provider profile as the API reports it.
 *
 * `apiKeyMask` is `null` when no key is stored; otherwise a short hint such as
 * `sk-…3f2a` — enough to recognise which key is configured, never enough to
 * use it.
 */
export const llmProviderSchema = z
  .object({
    id: entityIdSchema,
    siteId: entityIdSchema,
    /** The preset this profile was created from, or `null` for fully custom. */
    presetId: z.string().min(1).max(50).nullable(),
    name: z.string().min(1).max(100),
    baseUrl: z.string().min(1).max(500),
    apiKeyMask: z.string().max(20).nullable(),
    apiVersion: z.string().max(50).nullable(),
    defaultModel: z.string().max(200).nullable(),
    enabled: z.boolean(),
    /** This profile is the site's default for AI assistance (SPEC §74). */
    isDefault: z.boolean(),
    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export type LlmProvider = z.infer<typeof llmProviderSchema>;

/** `POST /api/sites/:siteId/llm/providers` (AGENTS.md §23). */
export const llmProviderCreateSchema = z
  .object({
    presetId: z
      .string()
      .regex(/^[a-z0-9][a-z0-9-]*$/)
      .min(1)
      .max(50)
      .optional(),
    name: z.string().min(1).max(100),
    /** Optional only when `presetId` supplies a base URL; checked in the service. */
    baseUrl: llmBaseUrlSchema.optional(),
    apiKey: z.string().max(500).optional(),
    apiVersion: z.string().min(1).max(50).optional(),
    defaultModel: z.string().min(1).max(200).optional(),
    enabled: z.boolean().optional(),
    isDefault: z.boolean().optional(),
  })
  .strict();

export type LlmProviderCreateInput = z.infer<typeof llmProviderCreateSchema>;

/**
 * `PUT /api/sites/:siteId/llm/providers/:providerId`.
 *
 * `apiKey` follows "absent means unchanged, empty string clears" so an edit
 * that only renames a provider never has to resend — or display — the key.
 * `null` clears `apiVersion` / `defaultModel`.
 */
export const llmProviderUpdateSchema = z
  .object({
    presetId: z
      .string()
      .regex(/^[a-z0-9][a-z0-9-]*$/)
      .min(1)
      .max(50)
      .nullable()
      .optional(),
    name: z.string().min(1).max(100).optional(),
    baseUrl: llmBaseUrlSchema.optional(),
    apiKey: z.string().max(500).optional(),
    apiVersion: z.string().min(1).max(50).nullable().optional(),
    defaultModel: z.string().min(1).max(200).nullable().optional(),
    enabled: z.boolean().optional(),
    isDefault: z.boolean().optional(),
  })
  .strict();

export type LlmProviderUpdateInput = z.infer<typeof llmProviderUpdateSchema>;

/** The result of `POST …/llm/providers/:providerId/verify`. */
export const llmVerifyResultSchema = z
  .object({
    verified: z.boolean(),
    /** Model ids the endpoint's `/models` reported, when it answered. */
    models: z.array(z.string()),
    /** HTTP status the endpoint answered with, when it answered. */
    status: z.number().int().optional(),
    /** Short human-readable reason, when verification failed. */
    error: z.string().max(300).optional(),
  })
  .strict();

export type LlmVerifyResult = z.infer<typeof llmVerifyResultSchema>;

/**
 * Build the display mask for a stored key.
 *
 * `null` for an absent key; very short keys collapse to dots only, because
 * `a…bcd` of a four-character secret is the whole secret.
 */
export function maskApiKey(apiKey: string): string | null {
  if (apiKey === "") {
    return null;
  }

  if (apiKey.length < 12) {
    return "••••••";
  }

  return `${apiKey.slice(0, 3)}…${apiKey.slice(-4)}`;
}

// ---------------------------------------------------------------------------
// AI assistance (SPEC §74): slug, tag and category generation
// ---------------------------------------------------------------------------

/**
 * Shared input of the generation commands.
 *
 * `content` is optional because a title alone often carries enough signal.
 * The cap follows the site model's context (128K tokens ≈ 100K+ Chinese
 * characters): the whole article travels, and the Worker truncates to its own
 * window before prompting.
 */
const llmGenerateInput = {
  title: z.string().min(1).max(300),
  content: z.string().max(200_000).optional(),
};

/**
 * `POST /api/sites/:siteId/llm/generate-slug` (AGENTS.md §10 — server decides).
 *
 * `locale` scopes the uniqueness check: slugs are unique per
 * `(site, locale, slug)`, so the generated URL is checked against the locale
 * the article will be published in.
 */
export const llmGenerateSlugSchema = z
  .object({ ...llmGenerateInput, locale: localeSchema.optional() })
  .strict();

export type LlmGenerateSlugInput = z.infer<typeof llmGenerateSlugSchema>;

/** `POST /api/sites/:siteId/llm/generate-tags`. */
export const llmGenerateTagsSchema = z.object(llmGenerateInput).strict();

export type LlmGenerateTagsInput = z.infer<typeof llmGenerateTagsSchema>;

/** `POST /api/sites/:siteId/llm/generate-category`. */
export const llmGenerateCategorySchema = z.object(llmGenerateInput).strict();

export type LlmGenerateCategoryInput = z.infer<typeof llmGenerateCategorySchema>;

/** `POST /api/sites/:siteId/llm/generate-description`. */
export const llmGenerateDescriptionSchema = z.object(llmGenerateInput).strict();

export type LlmGenerateDescriptionInput = z.infer<typeof llmGenerateDescriptionSchema>;

export type LlmGenerateInput = z.infer<typeof llmGenerateTagsSchema>;

/** The result of the slug command: a server-validated, canonical slug. */
export const llmSlugResultSchema = z
  .object({
    slug: slugSchema,
  })
  .strict();

export type LlmSlugResult = z.infer<typeof llmSlugResultSchema>;

/**
 * The result of the tag command.
 *
 * Tags are slugs (they become vocabulary rows), lowercase, without spaces.
 * The service guarantees: no duplicates, none colliding with the site's
 * existing tags, between `MIN` and `MAX` — unless the model simply cannot
 * produce that many usable ones, in which case fewer good tags beat made-up
 * ones.
 */
export const llmTagsResultSchema = z
  .object({
    tags: z.array(slugSchema).min(1).max(10),
  })
  .strict();

export type LlmTagsResult = z.infer<typeof llmTagsResultSchema>;

/** How many tags the command aims for (the prompt asks for this range). */
export const LLM_TAG_COUNT = { min: 5, max: 10 } as const;

/**
 * The result of the category command.
 *
 * `category` is either the slug of one of the site's existing categories or a
 * proposal for a new one; `isNew` says which, so the UI can label a suggestion
 * ("推荐新建") instead of silently writing a vocabulary row the user never saw.
 */
export const llmCategoryResultSchema = z
  .object({
    category: slugSchema,
    /** Display name for a proposed category; the slug when one was picked. */
    name: z.string().min(1).max(100),
    isNew: z.boolean(),
  })
  .strict();

export type LlmCategoryResult = z.infer<typeof llmCategoryResultSchema>;

/**
 * The result of the description command.
 *
 * The same string the frontmatter's `description` carries, so it obeys the
 * same bound the entry schema sets (500) — the SEO summary a search engine
 * shows under the title, not a second article.
 */
export const llmDescriptionResultSchema = z
  .object({
    description: z.string().min(1).max(500),
  })
  .strict();

export type LlmDescriptionResult = z.infer<typeof llmDescriptionResultSchema>;
